import { extendTheme } from "@chakra-ui/react";
import useColors from "./useColors";

const useCustomTheme = () => {
  
  const breakpoints = {
    "2xl": "1920px",
    xl: "1281px",
    lg: "960px", 
    base: "0px",
  };
  
  const { colors } = useColors();
    

  // Extend Chakra components
  const components = {
    Text: {
      baseStyle: {
        color: 'grey.500',
      },
    },
    Switch: {
      baseStyle: {
        track: {
          _checked: {
            bg: 'brand.700',
          },
        },
      },
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Input: {
      baseStyle: {
        field: {
          _focus: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px brand.500',
          },
          _focusVisible: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px brand.500',
          },
        },
      },
      defaultProps: {
        focusBorderColor: 'brand.500',
        colorScheme: 'brand',
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          borderWidth: '1px',
          borderRadius: '3px',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            color: 'white', // Checkmark color
          },
        },
      },
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Textarea: {
      baseStyle: {
        minHeight: "180px",
        boxShadow: '0 0 0 1px var(--borders)',
        padding: 4,
        _focus: {
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px brand.500',
        },
        _focusVisible: {
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px brand.500',
        },
      },
      defaultProps: {
        focusBorderColor: 'brand.400', 
        colorScheme: 'brand',
      },
    },
    Button: {
      variants: {
        solid: {
          bg: 'brand.700',
          color: 'white',
          _hover: {
            bg: 'brand.900',
          },
          _active: {
            bg: 'brand.900',
          },
        },
        ghost: {
          bg: 'transparent',
          color: 'grey.400',
          _hover: {
            bg: 'grey.100',
            color: 'grey.700',
          },
          _active: {
            bg: 'brand.50',
          },
        },
        outline: {
          borderColor: 'grey.200',
          color: 'grey.800',
          _hover: {
            bg: 'grey.50',
          },
        },
        link: {
          color: 'grey.700',
          _hover: {
            color: 'brand.400',
          },
        },
      },
      defaultProps: {
        colorScheme: 'brand',
      },
    },
  };

  const styles = {
    global: {
      "body": {
        color: "grey.800", // Default color for all other components
      }
    },
  };

  const theme = extendTheme({ breakpoints, colors, components, styles });

  const customReactSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      borderColor: state.isFocused ? theme.colors.brand[500] : theme.colors.grey[200],
      boxShadow: state.isFocused ? `0 0 0 1px ${theme.colors.brand[500]}` : "none",
      "&:hover": {
        borderColor: theme.colors.grey[300],
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? theme.colors.brand[100] : "#fff",
      color: state.isSelected ? theme.colors.brand[900] : theme.colors.grey[700],
      fontWeight: state.isSelected ? "500" : "400",
      "&:hover": {
        backgroundColor: theme.colors.brand[50],
        color: theme.colors.grey[700],
      },
      "&:active": {
        backgroundColor: theme.colors.brand[100],
        color: theme.colors.brand[900],
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.brand[50],
      fontWeight: "500",
    }),
  };
  

  return {
    breakpoints,
    theme,
    colors,
    customReactSelectStyles
  };
};

export default useCustomTheme;
